import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import sortBy from 'lodash/sortBy';

import { ActivityThemeStyles } from 'shared/types/Theme';

import { createTheme, getAllThemeNames, getThemeById, updateTheme } from './themeApi';

export type Theme = {
  _id: string;
  name: string;
  styles: ActivityThemeStyles;
  css: string;
};

export type ThemeNames = { _id: string; name: string }[];

export type CreateThemeDto = Omit<Theme, '_id'>;

export const useFetchAllThemeNames = () => {
  return useQuery<ThemeNames>({
    queryKey: ['themes', 'list'],
    queryFn: () => getAllThemeNames()
  });
};

export const useTheme = (themeId?: string | null) => {
  return useQuery<Theme>({
    queryKey: ['theme', { themeId }],
    queryFn: () => getThemeById(themeId as string),
    enabled: !!themeId
  });
};

type UseUpdateThemeArgs = {
  onSuccess: () => void;
  onError: () => void;
};

export function useUpdateTheme(props: UseUpdateThemeArgs) {
  const { onSuccess, onError } = props;

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateTheme,
    onSuccess: async (_, { _id }) => {
      await queryClient.invalidateQueries(['theme', { themeId: _id }]);
      onSuccess();
    },
    onError
  });
}

export function useCreateTheme(
  createThemeDto: CreateThemeDto,
  { onSuccess, onError }: { onSuccess: (themeId: string) => void; onError: () => void }
) {
  const queryClient = useQueryClient();

  return useMutation<Theme>({
    mutationFn: () => createTheme(createThemeDto),
    onSuccess: ({ _id, name }) => {
      void queryClient.setQueryData<ThemeNames>(['themes', 'list'], oldList =>
        sortBy([...(oldList ?? []), { _id, name }], ['name'])
      );
      onSuccess(_id);
    },
    onError
  });
}
